import { useEffect, useState } from 'react'
import { matchPath, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { CircleCheck, Globe, Lock, PencilLine, Radio, type LucideIcon } from 'lucide-react'
import { useWorkspaceOrgId } from '../lib/auth'
import { apiGet } from '../lib/api'
import { assessmentStatusLabel, onAssessmentStatusChange } from '../lib/assessmentStatus'
import { cn } from '../lib/utils'
import type { AssessmentStatus } from '../types'

const ICON: Record<AssessmentStatus, LucideIcon> = {
  draft: PencilLine,
  active: Radio,
  completed: CircleCheck,
  published: Globe,
  closed: Lock,
}

// ENG-509: draft vs live under the app header on every screen of an assessment,
// so no later screen reads as if invitations already went out.
export default function AssessmentStatusBar() {
  const { t } = useTranslation('admin')
  const orgId = useWorkspaceOrgId()
  const { pathname } = useLocation()
  const id = matchPath({ path: '/assessments/:id', end: false }, pathname)?.params.id
  const assessmentId = id && /^\d+$/.test(id) ? Number(id) : null
  const [loaded, setLoaded] = useState<{ id: number; status: AssessmentStatus } | null>(null)
  const [reloads, setReloads] = useState(0)

  useEffect(() => onAssessmentStatusChange(() => setReloads((n) => n + 1)), [])

  useEffect(() => {
    if (!orgId || assessmentId === null) return
    let cancelled = false
    apiGet<{ assessments: { id: number; status: AssessmentStatus }[] }>('/api/v1/assessments', orgId)
      .then((d) => {
        const a = d.assessments.find((x) => x.id === assessmentId)
        if (!cancelled && a) setLoaded({ id: a.id, status: a.status })
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [orgId, assessmentId, pathname, reloads])

  if (assessmentId === null || loaded?.id !== assessmentId) return null
  const { status } = loaded
  const live = status === 'active'
  const Icon = ICON[status]

  return (
    <div
      aria-label={t('assessments.statusBar.label')}
      role="region"
      className={cn(
        'flex shrink-0 items-start gap-2 border-b px-5 py-2 text-body md:items-center md:px-7 print:hidden',
        live ? 'bg-success-soft text-success'
          : status === 'draft' ? 'bg-warning-soft text-warning'
          : 'bg-muted text-muted-foreground',
      )}
    >
      <Icon aria-hidden className="mt-0.5 size-4 shrink-0 md:mt-0" />
      <p className="min-w-0">
        <span className={cn('font-medium', !live && status !== 'draft' && 'text-foreground')}>
          {live ? t('assessments.statusBar.live') : assessmentStatusLabel(status)}
        </span>
        {' · '}
        {t(`assessments.statusBar.${status}`)}
      </p>
    </div>
  )
}
